import { supabase } from '../lib/supabase';

const SITE_URL = 'https://thepsnewsletter.eu';

/**
 * Static pages with their priorities and change frequencies
 */
const STATIC_PAGES = [
  { path: '/', priority: 1.0, changefreq: 'weekly' },
  { path: '/newsletter-archive', priority: 0.9, changefreq: 'daily' },
];

/**
 * Format date to W3C datetime format for sitemap
 */
const formatDate = (date) => {
  return new Date(date)?.toISOString();
};

/**
 * Generate XML sitemap URL entry
 */
const generateUrlEntry = ({ loc, lastmod, changefreq, priority }) => {
  return `  <url>
    <loc>${loc}</loc>
    ${lastmod ? `<lastmod>${formatDate(lastmod)}</lastmod>` : ''}
    <changefreq>${changefreq}</changefreq>
    <priority>${priority}</priority>
  </url>`;
};

/**
 * Fetch all published newsletters from Supabase
 */
const fetchPublishedNewsletters = async () => {
  try {
    const { data, error } = await supabase?.from('newsletters')?.select('slug, updated_at, published_at')?.eq('status', 'published')?.order('published_at', { ascending: false });

    if (error) {
      console.error('Error fetching newsletters for sitemap:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Exception fetching newsletters for sitemap:', error);
    return [];
  }
};

/**
 * Generate complete XML sitemap
 */
export const generateSitemap = async () => {
  try {
    // Fetch dynamic newsletter pages
    const newsletters = await fetchPublishedNewsletters();

    // Build static pages URLs
    const staticUrls = STATIC_PAGES?.map(page =>
      generateUrlEntry({
        loc: `${SITE_URL}${page?.path}`,
        lastmod: new Date(),
        changefreq: page?.changefreq,
        priority: page?.priority,
      })
    );

    // Build newsletter pages URLs
    const newsletterUrls = newsletters?.map(newsletter =>
      generateUrlEntry({
        loc: `${SITE_URL}/newsletter/${newsletter?.slug}`,
        lastmod: newsletter?.updated_at || newsletter?.published_at,
        changefreq: 'monthly',
        priority: 0.8,
      })
    );

    // Combine all URLs
    const allUrls = [...staticUrls, ...newsletterUrls];

    // Generate complete sitemap XML
    const sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${allUrls?.join('\n')}
</urlset>`;

    return sitemap;
  } catch (error) {
    console.error('Error generating sitemap:', error);
    throw error;
  }
};

/**
 * Download sitemap as file (for manual generation)
 */
export const downloadSitemap = async () => {
  try {
    const sitemap = await generateSitemap();
    const blob = new Blob([sitemap], { type: 'application/xml' });
    const url = window.URL?.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sitemap.xml';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    window.URL?.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading sitemap:', error);
    throw error;
  }
};